// @flow
import { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import Select from '@tablekit/select';
import { SPACING } from '@tablekit/theme';
import { Z_INDICES } from 'utils/theme';
import { ShopListGrid as CoreGrid, gridElementStyles } from './styled';
import { JAPAN_PREFECTURE_ORDER_MAP } from './utils/constants';

type OptionsType = { label: string, value: string }[];

type PropsType = {
  areaOptions: OptionsType,
  areasLabel?: string,
  filterShops: (
    selectedAreas: string[],
    selectedTags: string[],
    selectedNames: string[]
  ) => void,
  nameOptions: OptionsType,
  namesLabel?: string,
  tagOptions: OptionsType,
  tagsLabel?: string
};

const FilterSelect = styled(Select)`
  ${gridElementStyles};
  z-index: ${({ position }) => Z_INDICES.MAX_CONTENT - position};
  margin-bottom: ${SPACING.L4};
`;

const FiltersGrid = styled(CoreGrid)`
  margin-bottom: ${SPACING.L4} !important;
`;

export const MAX_FILTERS = 3;

function updateFromSelect(selectedOptions, setState) {
  if (!selectedOptions) {
    setState([]);
    return;
  }
  let selectedOptionsArray = selectedOptions;
  if (typeof selectedOptionsArray.length === 'undefined') {
    selectedOptionsArray = [selectedOptions];
  }
  setState(selectedOptionsArray.map(({ value }) => value));
}

const Filters = ({
  areasLabel,
  tagsLabel,
  namesLabel,
  areaOptions,
  tagOptions,
  nameOptions,
  filterShops
}: PropsType) => {
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);

  const isUpdateRef = useRef(false);
  useEffect(() => {
    // this skips the initial render and only calls filter shops when the dependent
    // states change
    if (isUpdateRef.current) {
      filterShops(selectedAreas, selectedTags, selectedNames);
    } else {
      isUpdateRef.current = true;
    }
  }, [selectedAreas, selectedTags, selectedNames]);

  const sortedAreaOptions = [...areaOptions].sort(
    (a, b) =>
      JAPAN_PREFECTURE_ORDER_MAP[a.label] - JAPAN_PREFECTURE_ORDER_MAP[b.label]
  );

  const filters = [];

  if (nameOptions.length) {
    filters.push(
      <FilterSelect
        key="name"
        position={1}
        options={nameOptions}
        isClearable
        placeholder={namesLabel}
        onChange={(selectedOption) =>
          updateFromSelect(selectedOption, setSelectedNames)
        }
      />
    );
  }
  if (sortedAreaOptions.length && areasLabel) {
    filters.push(
      <FilterSelect
        key="area"
        position={filters.length + 1}
        options={sortedAreaOptions}
        isClearable
        placeholder={areasLabel}
        onChange={(selectedOption) =>
          updateFromSelect(selectedOption, setSelectedAreas)
        }
      />
    );
  }
  if (tagOptions.length && tagsLabel) {
    filters.push(
      <FilterSelect
        key="tags"
        position={filters.length + 1}
        options={tagOptions}
        isClearable
        placeholder={tagsLabel}
        onChange={(selectedOption) =>
          updateFromSelect(selectedOption, setSelectedTags)
        }
      />
    );
  }

  if (!filters.length) return null;

  return <FiltersGrid>{filters}</FiltersGrid>;
};

export default Filters;
